import PropTypes from "prop-types";
import BaseNode from "./BaseNode";
import { RealisticSplitAC } from "./realistic";

/**
 * SplitACNode – Split A/C indoor unit visualization.
 * Supports:
 * - Realistic 3D SCADA design with front cover digital LED temp & motorized louvers
 * - Glassmorphism design
 * - Classic industrial SVG layout
 */
const SplitACNode = (props) => {
  return (
    <BaseNode {...props}>
      {({ isRunning, isOffline, statusColor, speedScale, ratio, designType, color, opacity, liveValue, unit, width, height }) => {
        if (designType === "realistic") {
          return (
            <RealisticSplitAC
              liveValue={liveValue}
              unit={unit || "°C"}
              isRunning={isRunning}
              isOffline={isOffline}
              statusColor={statusColor}
              speedScale={speedScale}
              ratio={ratio}
              opacity={opacity}
              width={width}
              height={height}
            />
          );
        }

        const dotColor =
          statusColor === "green" ? "#22c55e" : statusColor === "red" ? "#ef4444" : "#94a3b8";
        const showOldDesign = designType === "new_design" || designType === "classic";

        return (
          <svg
            width="80"
            height="55"
            viewBox="0 0 80 55"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            style={{
              opacity,
              filter: isOffline ? "grayscale(100%) opacity(0.6)" : "none",
              overflow: "visible",
              transition: "all 0.3s ease",
            }}
          >
            {!showOldDesign ? (
              // ─── NEW PREMIUM GLASSMORPHIC DESIGN ───
              <>
                <defs>
                  <linearGradient id="acBodyGrad" x1="0" y1="0" x2="0" y2="32">
                    <stop offset="0%" stopColor="#f8fafc" />
                    <stop offset="70%" stopColor="#e2e8f0" />
                    <stop offset="100%" stopColor="#cbd5e1" />
                  </linearGradient>
                  <filter id="acGlow" x="-20%" y="-20%" width="140%" height="140%">
                    <feGaussianBlur stdDeviation="2" result="blur" />
                    <feComposite in="SourceGraphic" in2="blur" operator="over" />
                  </filter>
                </defs>

                {/* Main Enclosure */}
                <rect x="6" y="6" width="68" height="26" rx="4" fill="url(#acBodyGrad)" stroke="#94a3b8" strokeWidth="1.5" />

                {/* Louver opening at the bottom */}
                <rect x="10" y="26" width="60" height="4" rx="1.5" fill="#1e293b" />
                {/* Louver flap (angled when running) */}
                <line
                  x1="12"
                  y1={isRunning ? "29.5" : "28"}
                  x2="68"
                  y2={isRunning ? "29.5" : "28"}
                  stroke={isRunning ? color : "#64748b"}
                  strokeWidth="2.2"
                  strokeLinecap="round"
                  style={{ transition: "all 0.3s ease" }}
                />

                {/* Digital Temperature Screen */}
                <rect x="52" y="10" width="16" height="10" rx="1" fill="#0f172a" stroke="#475569" strokeWidth="0.5" />
                <text x="60" y="18" fill={isRunning ? "#22c55e" : "#64748b"} fontSize="7.5" fontFamily="monospace" fontWeight="extrabold" textAnchor="middle" filter={isRunning ? "url(#acGlow)" : "none"}>
                  {isRunning ? "24°" : "--"}
                </text>

                {/* Status LED indicators */}
                <circle cx="12" cy="12" r="1.5" fill={isRunning ? "#22c55e" : "#064e3b"} />
                <circle cx="16" cy="12" r="1.5" fill={isRunning ? "#eab308" : "#422006"} />

                {/* High-visibility Airflow Wind Waves */}
                {isRunning && (
                  <g filter="url(#acGlow)">
                    {/* Airflow 1 (Left) */}
                    <path d="M 18,30 Q 14,46 8,50" stroke={color} strokeWidth="2.5" strokeLinecap="round" fill="none" strokeDasharray="5 5">
                      <animate attributeName="stroke-dashoffset" values="10;0" dur="0.8s" repeatCount="indefinite" />
                    </path>
                    {/* Airflow 2 (Center) */}
                    <path d="M 40,30 Q 40,48 40,54" stroke={color} strokeWidth="3" strokeLinecap="round" fill="none" strokeDasharray="6 6">
                      <animate attributeName="stroke-dashoffset" values="12;0" dur="0.6s" repeatCount="indefinite" />
                    </path>
                    {/* Airflow 3 (Right) */}
                    <path d="M 62,30 Q 66,46 72,50" stroke={color} strokeWidth="2.5" strokeLinecap="round" fill="none" strokeDasharray="5 5">
                      <animate attributeName="stroke-dashoffset" values="10;0" dur="0.8s" repeatCount="indefinite" />
                    </path>
                  </g>
                )}

                {/* Premium status dot */}
                <circle cx="70" cy="10" r="4.5" fill={dotColor} stroke="#ffffff" strokeWidth="1" filter={isRunning ? "url(#acGlow)" : "none"}>
                  {isRunning && (
                    <animate attributeName="opacity" values="1;0.5;1" dur="1.2s" repeatCount="indefinite" />
                  )}
                </circle>
              </>
            ) : (
              // ─── CLASSIC INDUSTRIAL SVG DESIGN ───
              <>
                {/* AC body */}
                <rect x="5" y="5" width="70" height="28" rx="6" fill={`${color}10`} stroke={color} strokeWidth="2" />

                {/* Front panel louver */}
                <rect x="10" y="25" width="60" height="6" rx="2" fill={`${color}20`} stroke={color} strokeWidth="1" />

                {/* Louver slats */}
                <line x1="20" y1="27" x2="60" y2="27" stroke={color} strokeWidth="0.5" opacity="0.4" />
                <line x1="20" y1="29" x2="60" y2="29" stroke={color} strokeWidth="0.5" opacity="0.4" />

                {/* LED display area */}
                <rect x="55" y="10" width="14" height="10" rx="2" fill={`${color}15`} stroke={color} strokeWidth="0.5" />

                {/* Airflow lines (animated) */}
                {isRunning && (
                  <g style={{ animation: "live-ac-airflow 2s ease-in-out infinite" }}>
                    <path d="M20 33 Q25 42 30 33" stroke={color} strokeWidth="1" fill="none" opacity="0.4" />
                    <path d="M35 33 Q40 44 45 33" stroke={color} strokeWidth="1.5" fill="none" opacity="0.5" />
                    <path d="M50 33 Q55 42 60 33" stroke={color} strokeWidth="1" fill="none" opacity="0.4" />
                  </g>
                )}

                {/* Status indicator - top right */}
                <circle cx="70" cy="12" r="3" fill={dotColor} />

                {/* Temperature display */}
                <text x="62" y="18" textAnchor="middle" fontSize="7" fontWeight="bold" fill={color}>
                  {isRunning ? "24°" : "--"}
                </text>
              </>
            )}
            {/* Status text */}
            <text x="40" y="49" textAnchor="middle" fontSize="7.5" fontWeight="800" fill={dotColor} style={{ letterSpacing: 0.5 }}>
              {statusColor === "green" ? "COOLING" : statusColor === "red" ? "OFF" : "OFFLINE"}
            </text>
          </svg>
        );
      }}
    </BaseNode>
  );
};

SplitACNode.propTypes = { data: PropTypes.object, selected: PropTypes.bool, id: PropTypes.string };
export default SplitACNode;
